import { Component, OnDestroy, OnInit } from "@angular/core";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

import { AuthenticationService } from "@core/services/auth";
import { SchoolContextService } from "@core/services/school-context.service";
import { HelperFunctionsService } from "@core/services/helper-functions.service";
import { Role, SchoolContext, User, schoolContextKey } from "@core/models";

/**
 * The school name in the navbar, and - only where the teacher has more than one
 * context - the switch between them.
 *
 * A teacher at a hybrid school sees that school twice, once per curriculum, so
 * the curriculum is what tells the two entries apart. With a single context the
 * component renders the plain label the navbar has always shown.
 */
@Component({
  standalone: false,
  selector: "app-school-switcher",
  templateUrl: "./school-switcher.component.html",
  styleUrls: ["./school-switcher.component.scss"],
})
export class SchoolSwitcherComponent implements OnInit, OnDestroy {
  public contexts: SchoolContext[] = [];
  public isSwitching = false;

  private destroy$ = new Subject<boolean>();

  constructor(
    private _authenticationService: AuthenticationService,
    private _schoolContextService: SchoolContextService,
    private _helperFunctionsService: HelperFunctionsService
  ) {}

  ngOnInit(): void {
    // Only school staff have contexts to switch between; nobody else should be
    // calling the teacher endpoint.
    if (!this.isSchoolStaff) return;

    this._schoolContextService
      .getSchoolContexts()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (contexts) => (this.contexts = contexts ?? []),
        // Without a list there is nothing to switch to, and the plain school
        // name still renders - so there is nothing here the user could act on.
        error: () => (this.contexts = []),
      });
  }

  get currentUser(): User {
    return this._authenticationService.currentUserValue;
  }

  /** Unchanged from the navbar's own getter - the label shown when there is no choice. */
  get schoolName(): string {
    return this.currentUser?.currentSchool?.name?.trim() || "";
  }

  /**
   * Whether to offer a switch at all.
   *
   * One context is not a choice, and the overwhelming majority of users have
   * exactly one - they keep the plain label.
   */
  get hasChoice(): boolean {
    return this.contexts.length > 1;
  }

  /** Curriculum of the context the session is currently in, where it has one. */
  get activeCurriculum(): string {
    return this.contexts.find((context) => this.isActive(context))?.curriculum || "";
  }

  private get isSchoolStaff(): boolean {
    const role = this.currentUser?.userRole;
    return role === Role.Teacher || role === Role.CbcTeacher || role === Role.Admin;
  }

  /**
   * Whether `context` is the one the current token resolves to.
   *
   * The session reports its curriculum as `educationSystem`, so a hybrid
   * school's two entries are separated by that; a school running one curriculum
   * matches on the school alone.
   */
  isActive(context: SchoolContext): boolean {
    const user = this.currentUser;
    const activeSchoolId = user?.schoolId ?? user?.currentSchool?.id;
    if (!user || context?.schoolId !== activeSchoolId) return false;
    if (!context.curriculum) return true;

    return context.curriculum === (user.educationSystem || "").toUpperCase();
  }

  trackByContext(index: number, context: SchoolContext): string {
    return schoolContextKey(context);
  }

  /**
   * Switches into `context`.
   *
   * On success the page is reloaded: the new token scopes every school-bound
   * request, so everything already fetched belongs to the previous context and
   * a reload is the one refresh that misses nothing.
   */
  switchTo(context: SchoolContext): void {
    if (this.isSwitching || this.isActive(context)) return;

    this.isSwitching = true;
    this._schoolContextService
      .switchTo(context)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.reloadPage(),
        error: (error) => {
          this.isSwitching = false;
          // A 403 means the list no longer reflects what the account can reach.
          this._helperFunctionsService.showWarningToastr(
            error?.status === 403
              ? `You no longer have access to ${context.schoolName}.`
              : "Could not switch school. Please try again."
          );
        },
      });
  }

  /** Seam for tests, which must not reload the runner. */
  protected reloadPage(): void {
    window.location.reload();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
